/**
 * Task API Tools
 *
 * Defines client-side agentic tools that wrap the CefSharp-bound TaskApi.
 * Each tool maps to a method on the C# TaskApi class exposed via CefSharp.
 *
 * CefSharp specifics:
 *   - Methods are camelCase on the JS side
 *   - All methods return Promise<string> (JSON-encoded)
 *   - Arguments must be passed positionally (not as an object)
 */

import { toToolDisplayName, type RuntimeToolDef } from "./runtimeTypes";
import { getAPI } from "../services/workspaceService";
import { invokeCefSharpMethod } from "../services/cefSharpBinding";

const TIMEOUT_MS = 30_000;
const DEFAULT_PAGE_SIZE = 100;

// ---------------------------------------------------------------------------
// Tool specifications
// ---------------------------------------------------------------------------

interface TaskApiToolSpec {
  /** Method name on the JS-side taskApi object (camelCase). */
  method: string;
  /** Tool name exposed to the agent. */
  name: string;
  description: string;
  deferred?: boolean;
  parameters: RuntimeToolDef["definition"]["parameters"];
  /** Ordered argument names matching the C# method signature. */
  argOrder: string[];
  /** If true, this tool returns a list and uses client-side pagination. */
  paginated?: boolean;
  /** For paginated reserved-task tools, include reservation columns. */
  reservationColumns?: boolean;
}

const TOOL_SPECS: TaskApiToolSpec[] = [
  // ── Read — Tasks ──────────────────────────────────────────────────────
  {
    method: "getTasks",
    name: "WS-taskApi-getTasks",
    description:
      "Returns a paginated markdown table of all task definitions in the current Quadri model. " +
      "Response includes totalCount, offset, count, and a compact markdown table. " +
      "Use offset/limit to page through results. " +
      "Use WS-taskApi-getTasksInFolder or WS-taskApi-getTasksByName to narrow results.",
    parameters: {
      type: "object",
      properties: {
        offset: {
          type: "number",
          description: "Zero-based index to start from (default 0).",
        },
        limit: {
          type: "number",
          description: "Maximum number of tasks to return (default 100).",
        },
      },
      required: [],
    },
    argOrder: [],
    paginated: true,
  },
  {
    method: "getTask",
    name: "WS-taskApi-getTask",
    description:
      "Returns full detail for a single task definition, including its properties and a list of executions.",
    deferred: false,
    parameters: {
      type: "object",
      properties: {
        guid: {
          type: "string",
          description: "The GUID of the task definition.",
        },
      },
      required: ["guid"],
    },
    argOrder: ["guid"],
  },
  {
    method: "getTaskExecutions",
    name: "WS-taskApi-getTaskExecutions",
    description:
      "Returns a JSON array of executions for the task definition identified by its GUID.",
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description: "The GUID of the task definition.",
        },
      },
      required: ["taskGuid"],
    },
    argOrder: ["taskGuid"],
  },
  {
    method: "getTaskExecution",
    name: "WS-taskApi-getTaskExecution",
    description:
      "Returns full detail for a single task execution, including all properties.",
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description: "The GUID of the task definition.",
        },
        executionId: {
          type: "number",
          description: "The numeric ID of the execution.",
        },
      },
      required: ["taskGuid", "executionId"],
    },
    argOrder: ["taskGuid", "executionId"],
  },

  // ── Read — Schema ─────────────────────────────────────────────────────
  {
    method: "getTaskSchema",
    name: "WS-taskApi-getTaskSchema",
    description:
      "Returns the JSON schema (TaskTypeDefinition) for a task type identified by its tag. " +
      "Includes property names, types, captions, legal values, etc.",
    parameters: {
      type: "object",
      properties: {
        tag: {
          type: "string",
          description: "The task type tag (e.g. from a task's 'tag' field).",
        },
      },
      required: ["tag"],
    },
    argOrder: ["tag"],
  },

  // ── Read — Properties ─────────────────────────────────────────────────
  {
    method: "getTaskProperty",
    name: "WS-taskApi-getTaskProperty",
    description: "Returns a single property value from a task definition.",
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description: "The GUID of the task definition.",
        },
        propertyName: {
          type: "string",
          description: "The property name as defined in the task schema.",
        },
      },
      required: ["taskGuid", "propertyName"],
    },
    argOrder: ["taskGuid", "propertyName"],
  },
  {
    method: "getTaskExecutionProperty",
    name: "WS-taskApi-getTaskExecutionProperty",
    description: "Returns a single property value from a task execution.",
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description: "The GUID of the task definition.",
        },
        executionId: {
          type: "number",
          description: "The numeric ID of the execution.",
        },
        propertyName: {
          type: "string",
          description: "The property name as defined in the task schema.",
        },
      },
      required: ["taskGuid", "executionId", "propertyName"],
    },
    argOrder: ["taskGuid", "executionId", "propertyName"],
  },

  // ── Read — Search ─────────────────────────────────────────────────────
  {
    method: "getTasksByName",
    name: "WS-taskApi-getTasksByName",
    description:
      "Searches for task definitions by name (case-insensitive substring match). " +
      "Returns a paginated markdown table of matching tasks. " +
      "Use offset/limit to page through results.",
    deferred: false,
    parameters: {
      type: "object",
      properties: {
        name: {
          type: "string",
          description: "The search string to match against task names.",
        },
        offset: {
          type: "number",
          description: "Zero-based index to start from (default 0).",
        },
        limit: {
          type: "number",
          description: "Maximum number of tasks to return (default 100).",
        },
      },
      required: ["name"],
    },
    argOrder: ["name"],
    paginated: true,
  },
  {
    method: "getTasksInFolder",
    name: "WS-taskApi-getTasksInFolder",
    description:
      'Returns a paginated markdown table of all task definitions within a folder path (e.g. "10 IMPORT"). ' +
      "Recursively includes tasks in subfolders. " +
      "Use offset/limit to page through results.",
    parameters: {
      type: "object",
      properties: {
        folderPath: {
          type: "string",
          description:
            'The folder path to list tasks from (e.g. "10 IMPORT"). Empty string for root.',
        },
        offset: {
          type: "number",
          description: "Zero-based index to start from (default 0).",
        },
        limit: {
          type: "number",
          description: "Maximum number of tasks to return (default 100).",
        },
      },
      required: ["folderPath"],
    },
    argOrder: ["folderPath"],
    paginated: true,
  },
  {
    method: "getTaskFolders",
    name: "WS-taskApi-getTaskFolders",
    description:
      "Returns the complete current Quadri task explorer folder hierarchy, including empty folders. " +
      "Each folder includes its path, parentPath, depth, direct and recursive task counts, subfolder count, and restriction status. " +
      "Use this before WS-taskApi-moveTask when the user has not specified a destination, then choose a non-restricted folder whose name and neighboring tasks fit the source task's name, type, purpose, and dependencies.",
    parameters: {
      type: "object",
      properties: {},
      required: [],
    },
    argOrder: [],
  },

  // ── Read — Dependencies ───────────────────────────────────────────────
  {
    method: "getTaskDependencies",
    name: "WS-taskApi-getTaskDependencies",
    description:
      "Returns a JSON object with predecessor and successor task lists for the given task. " +
      "Includes guid, name, description, tag, folderPath for each dependency.",
    deferred: false,
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description: "The GUID of the task definition.",
        },
      },
      required: ["taskGuid"],
    },
    argOrder: ["taskGuid"],
  },

  // ── Read — Reservations ────────────────────────────────────────────────
  {
    method: "getTaskReservation",
    name: "WS-taskApi-getTaskReservation",
    description:
      "Returns reservation info for a single task definition, including whether it is reserved, " +
      "who reserved it, and the reservation status (Undefined, Added, ReservedAndModified, Locked, Reserved).",
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description: "The GUID of the task definition.",
        },
      },
      required: ["taskGuid"],
    },
    argOrder: ["taskGuid"],
  },
  {
    method: "getReservedTasks",
    name: "WS-taskApi-getReservedTasks",
    description:
      "Returns a paginated markdown table of all reserved tasks across the entire model. " +
      "Includes reservation status columns. " +
      "Use offset/limit to page through results.",
    parameters: {
      type: "object",
      properties: {
        offset: {
          type: "number",
          description: "Zero-based index to start from (default 0).",
        },
        limit: {
          type: "number",
          description: "Maximum number of tasks to return (default 100).",
        },
      },
      required: [],
    },
    argOrder: [],
    paginated: true,
    reservationColumns: true,
  },
  {
    method: "getReservedTasksInFolder",
    name: "WS-taskApi-getReservedTasksInFolder",
    description:
      'Returns a paginated markdown table of reserved tasks within a folder path (e.g. "101 FRA SOSI"). ' +
      "Includes reservation status columns. " +
      "Use offset/limit to page through results.",
    parameters: {
      type: "object",
      properties: {
        folderPath: {
          type: "string",
          description:
            'The folder path to search (e.g. "101 FRA SOSI"). Empty string for root.',
        },
        offset: {
          type: "number",
          description: "Zero-based index to start from (default 0).",
        },
        limit: {
          type: "number",
          description: "Maximum number of tasks to return (default 100).",
        },
      },
      required: ["folderPath"],
    },
    argOrder: ["folderPath"],
    paginated: true,
    reservationColumns: true,
  },

  // ── Read — Folder summary ─────────────────────────────────────────────
  {
    method: "getTaskFolderSummary",
    name: "WS-taskApi-getTaskFolderSummary",
    description:
      "Returns a full summary for a folder path: total task count, direct children, " +
      "subfolder breakdown, reserved tasks, feature/net element totals, and tasks-by-type grouping.",
    parameters: {
      type: "object",
      properties: {
        folderPath: {
          type: "string",
          description:
            'The folder path to summarize (e.g. "101 FRA SOSI"). Empty string for root.',
        },
      },
      required: ["folderPath"],
    },
    argOrder: ["folderPath"],
  },

  // ── Read — Object counts ──────────────────────────────────────────────
  {
    method: "getTaskObjectCount",
    name: "WS-taskApi-getTaskObjectCount",
    description:
      "Returns a JSON object with the number of features and net elements " +
      "associated with a task. Fields: featureCount, netElementCount.",
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description: "The GUID of the task definition.",
        },
      },
      required: ["taskGuid"],
    },
    argOrder: ["taskGuid"],
  },
  {
    method: "getTaskFolderObjectCount",
    name: "WS-taskApi-getTaskFolderObjectCount",
    description:
      "Returns a JSON object with the total number of features and net elements " +
      'across all tasks within a folder path (e.g. "Folder1/SubFolder2"). ' +
      "Fields: featureCount, netElementCount.",
    parameters: {
      type: "object",
      properties: {
        folderPath: {
          type: "string",
          description:
            'The folder path to aggregate counts for (e.g. "Folder1/SubFolder2"). Empty string for root.',
        },
      },
      required: ["folderPath"],
    },
    argOrder: ["folderPath"],
  },

  // ── Write — Structure ────────────────────────────────────────────────
  {
    method: "moveTask",
    name: "WS-taskApi-moveTask",
    description:
      "Immediately moves an existing task definition to another folder in the current Quadri task tree. " +
      "If the user did not specify a destination, first call WS-taskApi-getTaskFolders and inspect likely folders with WS-taskApi-getTasksInFolder; choose a non-restricted destination based on the source task's name, type, purpose, dependencies, and neighboring tasks. " +
      "For two or more tasks, use WS-taskApi-moveTasks instead of calling this tool repeatedly. " +
      "Use task discovery tools first if the source GUID or destination path is ambiguous. " +
      "If ACTIVE_TOOL is returned, ask the user to finish or cancel the active Novapoint editing tool before retrying. " +
      "Pass an empty destinationFolderPath to move the task to the root. This changes hierarchy only, not geometry.",
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description: "The GUID of the task definition to move.",
        },
        destinationFolderPath: {
          type: "string",
          description:
            'Slash-delimited path of an existing destination folder, or "" for root.',
        },
      },
      required: ["taskGuid", "destinationFolderPath"],
    },
    argOrder: ["taskGuid", "destinationFolderPath"],
  },
  {
    method: "moveTasks",
    name: "WS-taskApi-moveTasks",
    description:
      "Immediately moves multiple task definitions to one existing folder in a single Quadri model update. " +
      "Use this whenever the user asks to move two or more tasks; do not call WS-taskApi-moveTask repeatedly. " +
      "If the destination is unspecified, first call WS-taskApi-getTaskFolders and inspect likely folders with WS-taskApi-getTasksInFolder. " +
      "Returns requestedCount, movedCount, failedCount, and a result for each GUID. Tasks that fail validation are reported without preventing valid tasks from moving. " +
      "If ACTIVE_TOOL is returned, ask the user to finish or cancel the active Novapoint editing tool before retrying. " +
      "Pass an empty destinationFolderPath for root. This changes hierarchy only, not geometry.",
    parameters: {
      type: "object",
      properties: {
        taskGuids: {
          type: "array",
          items: { type: "string" },
          minItems: 1,
          description:
            "Unique GUIDs of all task definitions to move in this batch.",
        },
        destinationFolderPath: {
          type: "string",
          description:
            'Slash-delimited path of an existing destination folder, or "" for root.',
        },
      },
      required: ["taskGuids", "destinationFolderPath"],
    },
    argOrder: ["taskGuids", "destinationFolderPath"],
  },
  {
    method: "copyTask",
    name: "WS-taskApi-copyTask",
    description:
      "Immediately creates a new task definition with a new GUID in an existing destination folder. " +
      "Use task discovery tools first if the source GUID or destination path is ambiguous. " +
      "If ACTIVE_TOOL is returned, ask the user to finish or cancel the active Novapoint editing tool before retrying. " +
      "Copies definition properties and optionally dependencies, but never executions, result objects, attachments, or composite descendants.",
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description: "The GUID of the source task definition.",
        },
        destinationFolderPath: {
          type: "string",
          description:
            'Slash-delimited path of an existing destination folder, or "" for root.',
        },
        newName: {
          type: "string",
          description: "A non-empty name that does not already exist in the destination.",
        },
        copyDependencies: {
          type: "boolean",
          description:
            "Whether to preserve predecessor-aware query properties and explicit predecessor links.",
        },
      },
      required: [
        "taskGuid",
        "destinationFolderPath",
        "newName",
        "copyDependencies",
      ],
    },
    argOrder: [
      "taskGuid",
      "destinationFolderPath",
      "newName",
      "copyDependencies",
    ],
  },
  {
    method: "duplicateTask",
    name: "WS-taskApi-duplicateTask",
    description:
      'Immediately duplicates a task definition beside its source with a new GUID and a generated "(Copy)" name. ' +
      "Preserves definition properties, dependencies, and independent copies of attached project files, but never executions, result objects, attachment blobs, or composite descendants. " +
      "If ACTIVE_TOOL is returned, ask the user to finish or cancel the active Novapoint editing tool before retrying. " +
      "Use task discovery tools first if the source GUID is ambiguous.",
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description: "The GUID of the task definition to duplicate.",
        },
      },
      required: ["taskGuid"],
    },
    argOrder: ["taskGuid"],
  },
];

// ---------------------------------------------------------------------------
// Markdown formatting helpers
// ---------------------------------------------------------------------------

function formatTasksMarkdownTable(tasks: any[]): string {
  const header = "| Name | Tag | Folder | Last Modified | GUID |";
  const separator = "|---|---|---|---|---|";
  const rows = tasks.map((t) => {
    const name = (t.name || "").replace(/\|/g, "\\|");
    const tag = t.tag || "";
    const folder = (t.folderPath || "").replace(/\|/g, "\\|");
    const modified = t.lastModified ? t.lastModified.slice(0, 10) : "";
    const guid = t.guid || "";
    return `| ${name} | ${tag} | ${folder} | ${modified} | ${guid} |`;
  });
  return [header, separator, ...rows].join("\n");
}

function formatReservedTasksMarkdownTable(tasks: any[]): string {
  const header = "| Name | Tag | Folder | Reserved By | Status | GUID |";
  const separator = "|---|---|---|---|---|---|";
  const rows = tasks.map((t) => {
    const name = (t.name || "").replace(/\|/g, "\\|");
    const tag = t.tag || "";
    const folder = (t.folderPath || "").replace(/\|/g, "\\|");
    const reservedBy = (t.reservedBy || "").replace(/\|/g, "\\|");
    const status = t.reservationStatus || "";
    const guid = t.guid || "";
    return `| ${name} | ${tag} | ${folder} | ${reservedBy} | ${status} | ${guid} |`;
  });
  return [header, separator, ...rows].join("\n");
}

function buildPaginatedResponse(
  allItems: any[],
  offset: number,
  limit: number,
  toolName: string,
  reservationColumns: boolean,
): string {
  const totalCount = allItems.length;
  const page = allItems.slice(offset, offset + limit);
  const count = page.length;
  const end = offset + count;

  const table = reservationColumns
    ? formatReservedTasksMarkdownTable(page)
    : formatTasksMarkdownTable(page);

  let hint = `Showing tasks ${offset + 1}–${end} of ${totalCount}.`;
  if (end < totalCount) {
    hint += ` To see more, call ${toolName} again with offset=${end}.`;
  }
  hint +=
    " You can also use WS-taskApi-getTasksInFolder or WS-taskApi-getTasksByName to narrow results.";

  return JSON.stringify({
    success: true,
    data: { totalCount, offset, count, hint, tasks: table },
  });
}

// ---------------------------------------------------------------------------
// CefSharp-aware callback factory
// ---------------------------------------------------------------------------

/**
 * Creates a callback that invokes a CefSharp-bound taskApi method.
 *
 * Unlike the generic WS dispatcher, this:
 *   1. Extracts arguments positionally (CefSharp mirrors C# signatures)
 *   2. JSON.parses the string result (CefSharp methods return JSON strings)
 *   3. Wraps the parsed data in the standard { success, data, error } envelope
 */
function createCefSharpCallback(
  method: string,
  argOrder: string[],
): (args: Record<string, unknown>) => Promise<string> {
  return async (args: Record<string, unknown>): Promise<string> => {
    try {
      const api = getAPI();
      const taskApi = api?.taskApi;
      if (!taskApi) {
        return JSON.stringify({
          success: false,
          error:
            "TaskApi not bound — CefSharp.BindObjectAsync may not have completed.",
        });
      }

      const fn = taskApi[method];
      if (typeof fn !== "function") {
        return JSON.stringify({
          success: false,
          error: `Unknown method: taskApi.${method}`,
        });
      }

      // Extract positional arguments in the order the C# method expects
      const positionalArgs = argOrder.map((name) => args?.[name]);
      const raw = await invokeCefSharpMethod<string>(
        "taskApi",
        taskApi,
        method,
        positionalArgs,
      );

      // CefSharp methods return JSON strings — parse before wrapping
      let data: unknown;
      try {
        data = JSON.parse(raw);
      } catch {
        data = raw;
      }

      return JSON.stringify({ success: true, data });
    } catch (error) {
      return JSON.stringify({
        success: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };
}

/**
 * Creates a paginated callback that fetches all items from the C# API,
 * then slices client-side and returns a markdown-formatted table.
 */
function createPaginatedCallback(
  method: string,
  argOrder: string[],
  toolName: string,
  reservationColumns: boolean,
): (args: Record<string, unknown>) => Promise<string> {
  return async (args: Record<string, unknown>): Promise<string> => {
    try {
      const api = getAPI();
      const taskApi = api?.taskApi;
      if (!taskApi) {
        return JSON.stringify({
          success: false,
          error:
            "TaskApi not bound — CefSharp.BindObjectAsync may not have completed.",
        });
      }

      const fn = taskApi[method];
      if (typeof fn !== "function") {
        return JSON.stringify({
          success: false,
          error: `Unknown method: taskApi.${method}`,
        });
      }

      // Extract positional arguments (exclude offset/limit — those are pagination-only)
      const positionalArgs = argOrder.map((name) => args?.[name]);
      const raw = await invokeCefSharpMethod<string>(
        "taskApi",
        taskApi,
        method,
        positionalArgs,
      );

      let data: unknown;
      try {
        data = JSON.parse(raw);
      } catch {
        data = raw;
      }

      if (!Array.isArray(data)) {
        return JSON.stringify({ success: true, data });
      }

      const offset = Math.max(0, Number(args?.offset) || 0);
      const limit = Math.max(1, Number(args?.limit) || DEFAULT_PAGE_SIZE);

      return buildPaginatedResponse(
        data,
        offset,
        limit,
        toolName,
        reservationColumns,
      );
    } catch (error) {
      return JSON.stringify({
        success: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create RuntimeToolDef entries for every TaskApi method.
 * Call this after `CefSharp.BindObjectAsync("taskApi")` has resolved.
 */
export function createTaskApiTools(): Record<string, RuntimeToolDef> {
  const tools: Record<string, RuntimeToolDef> = {};

  for (const spec of TOOL_SPECS) {
    const callback = spec.paginated
      ? createPaginatedCallback(
        spec.method,
        spec.argOrder,
        spec.name,
        spec.reservationColumns ?? false,
      )
      : createCefSharpCallback(spec.method, spec.argOrder);

    tools[spec.name] = {
      definition: {
        name: spec.name,
        description: spec.description,
        deferred: spec.deferred ?? true,
        title: toToolDisplayName(spec.name),
        parameters: spec.parameters,
      },
      callback,
      timeOutInMs: TIMEOUT_MS,
    };
  }

  return tools;
}
